import { API_URL } from '../config';
import { getMessage } from '../data/locales';
import { getBizumCheckoutErrorKey } from '../utils/bizumCheckout';
import { mapBillingAddressToStripe, mapBillingDetailsToStripe } from '../utils/mapBillingDetailsToStripe';

/**
 * Starts the payment process and awaits confirmation.
 * @param {Object} params
 * @param {string} params.orderId
 * @param {string} params.method - 'card', 'bizum', 'financing'
 * @param {Object} params.billingDetails - { email, name, ... }
 * @param {number} [params.selectedInstallments]
 * @param {string} [params.financingProvider] - 'klarna' or 'alma'
 * @param {Object} params.stripe - Stripe instance
 * @param {Object} [params.elements] - Stripe Elements instance (required for card; uses mounted Payment Element)
 * @param {string} [params.clientSecret] - Optional: if provided, skips create-intent
 * @param {Function} [params.onProcessing]
 * @param {Function} [params.onError]
 * @returns {Promise<{status: string, order: Object}>}
 */
export const startPaymentAndAwaitConfirmation = async ({
  orderId,
  method,
  billingDetails,
  selectedInstallments,
  financingProvider,
  stripe,
  elements,
  clientSecret,
  onProcessing = () => {},
  onError = () => {},
}) => {
  try {
    if (method === 'bizum') {
      const bizumErrorKey = getBizumCheckoutErrorKey(billingDetails);
      if (bizumErrorKey) {
        const message = getMessage(bizumErrorKey);
        onError(message);
        return { status: 'failed', error: message };
      }
    }

    // Card + Payment Element: never call onProcessing before confirmPayment. CheckoutPage uses it to
    // replace the whole PaymentMethods block with a spinner, which unmounts <Elements> and breaks
    // stripe.confirmPayment (IntegrationError: no mounted Payment Element). Bizum/financing do not
    // depend on mounted Elements for that same call path.
    if (method !== 'card') {
      onProcessing();
    }

    let secret = clientSecret;

    if (!secret) {
      console.log("[payments.js] No client secret provided, creating new intent...");
      const paymentMethodType = method === 'financing' ? financingProvider : method;
      const intentRes = await fetch(`${API_URL}/api/pagos/stripe/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          orderId,
          paymentMethodType,
          selectedInstallments,
          ...(method === 'bizum' ? { phone: billingDetails?.phone } : {}),
        }),
      });

      const intentData = await intentRes.json();
      console.log(`[payments.js] Intent creation response: ${intentRes.status}`, intentData);

      if (!intentRes.ok) throw new Error(intentData.error || 'Error creating payment intent');
      
      secret = intentData.client_secret;
    }

    if (!secret) throw new Error('No client secret received');

    console.log(`[payments.js] Confirming payment with method: ${method}`);

    // 2. Confirm Payment
    let paymentResult;
    
    if (method === 'card') {
      if (!elements) throw new Error('Stripe Elements required for card payment');
      if (!elements.getElement('payment')) {
        throw new Error(
          'El formulario de pago no está listo. Espera a que termine de cargar e inténtalo de nuevo.'
        );
      }

      // Client secret is already bound via <Elements options={{ clientSecret }} />; do not pass it again here.
      console.log('[payments.js] Calling stripe.confirmPayment (Payment Element)...');
      paymentResult = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success?orderId=${orderId}`,
          payment_method_data: {
            billing_details: mapBillingAddressToStripe(billingDetails),
          },
        },
        redirect: 'if_required',
      });
    } else {
      // For Bizum, Klarna, etc. The PaymentIntent already lists payment_method_types.
      console.log(`[payments.js] Calling stripe.confirmPayment for ${method}...`);
      const paymentMethodType = method === 'financing' ? financingProvider : method;
      paymentResult = await stripe.confirmPayment({
        clientSecret: secret,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success?orderId=${orderId}`,
          payment_method_data: {
            type: paymentMethodType,
            billing_details: mapBillingDetailsToStripe(billingDetails),
          },
        },
        redirect: 'if_required',
      });
    }

    console.log('[payments.js] Stripe PaymentIntent confirmation result:', paymentResult);
    const { error, paymentIntent } = paymentResult;

    if (error) {
      console.error("[payments.js] Confirm Payment Error:", error);
      throw new Error(error.message);
    }

    if (paymentIntent.next_action?.redirect_to_url) {
      console.log("[payments.js] Redirecting to:", paymentIntent.next_action.redirect_to_url.url);
      window.location.href = paymentIntent.next_action.redirect_to_url.url;
      return { status: 'redirecting' };
    }

    if (paymentIntent.status === 'processing' || paymentIntent.status === 'succeeded') {
      console.log(`[payments.js] Payment status: ${paymentIntent.status}`);
      // 3. Return processing status so UI can start polling
      return { status: 'processing', order: { _id: orderId } };
    } else {
      console.warn(`[payments.js] Unexpected status: ${paymentIntent.status}`);
      throw new Error(`Estado inesperado: ${paymentIntent.status}`);
    }

  } catch (err) {
    console.error("Payment error:", err);
    onError(err.message);
    return { status: 'failed', error: err.message };
  }
};

